import { createContext, useContext, useEffect, useReducer } from 'react';
import ajaxSchool from '../util/remote/ajaxSchool';
import functions from '../util/functions';

const initialState = { 
    transactionListing: null,
    metaData: null,
    currentPage: 1,
    classId: '0',
    openingDate: '',
    closingDate: '',
    searchText: '',
    selectedIds: [],
    reset: false,

    recentTransactions: null,
    billedRatio: null,
    discountedSum: 0,
    billedSum: 0,
    paidSum: 0,
    collectionOverview: null,
    mounted: false,
    showDelete: false,
    grossBalanceRatio: null,
    billingSummary: null,
    loading: false

};

const reducer = (state, action) => {
    switch (action.type) {
      case 'CLEAR':
        return { ...initialState }; // Reset state completely
      case 'SET_SELECTED_IDS': // Handle updates to selectedIds
        return { ...state, selectedIds: action.payload };
      default:
        return { ...state, [action.type]: action.payload };
    }
  };


const FeesTransactionContext = createContext();

export const FeesTransactionProvider = ({children}) =>{
  const [state, dispatch] = useReducer(reducer, initialState);


  /**
   * Only call this if the getTransactions has already been called on mount by the FeesTransactionsPage
   */
  useEffect(()=>{
    if(state.mounted){
      getTransactions(true);
    }
  }, [state.currentPage])

 
  const getTransactions = async (refresh = false) => {

        /**
         * Mark that the method has been called by a mounted component
         */
        dispatch({
          type: "mounted",
          payload: true
        });

        if(Array.isArray(state.transactionListing) && !refresh){
          return
        }
          dispatch({
            type: "transactionListing",
            payload: false
          });

        const payloadData = {
            class_id: state.classId,
            opening_date: state.openingDate,
            closing_date: state.closingDate,
            search_text: state.searchText,
            page: state.currentPage, // Use page 1 if resetting
        };
        dispatch({ type: "loading", payload: true });
        const server_response = await ajaxSchool.fetchAllTransactions(payloadData);
        dispatch({ type: "loading", payload: false });
        if (server_response.status === "OK") {

            dispatch({
                type: "transactionListing",
                payload: server_response.details.data
              });

            // Update metaData with the response meta information
            dispatch({ type: "metaData", payload: server_response.details.meta });
        } else {
            // Handle error response
            dispatch({ type: "transactionListing", payload: "404" });
        }
   
};



  const setCurrentPage = (page)=>{
    dispatch({ type: 'currentPage', payload: page });
  }

  const filter = async()=>{
    await getTransactions(true);
  }


  const getRecentTransactions = async(refresh)=>{
    if(Array.isArray(state.recentTransactions) && !refresh){
        return;
      }
    const server_response = await ajaxSchool.fetchRecentTransactions();
    if(server_response.status === "OK"){
        dispatch({ type: "recentTransactions", payload: server_response.details });

    }else{
        dispatch({ type: "recentTransactions", payload: "404" });
    }
  }


  const getBilledRatio = async(refresh)=>{
    if(functions.isObject(state.billedRatio) && !refresh){
        return;
      }
    const server_response = await ajaxSchool.fetchBilledRatio();
    if(server_response.status === "OK"){
        dispatch({ type: "billedRatio", payload: server_response.details });
    }
  }

  const getBilledSum = async(refresh)=>{
    if(state.billedSum && !refresh){
        return;
      }
    const server_response = await ajaxSchool.fetchBilledSum();
    if(server_response.status === "OK"){
        dispatch({ type: "billedSum", payload: server_response.details });
    }
  }

  const getPaidSum = async(refresh)=>{
    if(state.paidSum && !refresh){
        return;
      }
    const server_response = await ajaxSchool.fetchPaidSum();
    if(server_response.status === "OK"){
        dispatch({ type: "paidSum", payload: server_response.details });
    }
  }

  const getDiscountedSum = async(refresh)=>{
    if(state.discountedSum && !refresh){
        return;
      }
    const server_response = await ajaxSchool.fetchDiscountedSum();
    if(server_response.status === "OK"){
        dispatch({ type: "discountedSum", payload: server_response.details });
    }
  }

  const getCollectionOverview = async(refresh)=>{
    if(state.collectionOverview && !refresh){
        return;
      }
    const server_response = await ajaxSchool.fetchCollectionOverview();
    if(server_response.status === "OK"){
        dispatch({ type: "collectionOverview", payload: server_response.details });
    }else{
        dispatch({ type: "collectionOverview", payload: '404' });
    }
  }

  const getGrossBalanceRatio = async(refresh)=>{
    if(state.grossBalanceRatio && !refresh){
        return;
      }
    const server_response = await ajaxSchool.fetchGrossBalanceRatio();
    if(server_response.status === "OK"){
        dispatch({ type: "grossBalanceRatio", payload: server_response.details });
    }else{
        dispatch({ type: "grossBalanceRatio", payload: '404' });
    }
  }

  const getBillingSummary = async(refresh)=>{
    if(state.billingSummary && !refresh){
        return;
      }
    const server_response = await ajaxSchool.fetchBillingSummary();
    if(server_response.status === "OK"){
        dispatch({ type: "billingSummary", payload: server_response.details });
    }else{
        dispatch({ type: "billingSummary", payload: '404' });
    }
  }
  
  const refresh = async()=>{
      await getRecentTransactions(true);
      await getBilledRatio(true);
      await getTransactions(true);
      await getGrossBalanceRatio(true);
      await getBillingSummary(true);
  }

  const setSelectedIds = (ids) => {
    dispatch({ type: 'SET_SELECTED_IDS', payload: ids });
  };

  const resetSelectedIds = () => {
      dispatch({ type: 'SET_SELECTED_IDS', payload: [] });
  };

  const toggleDelete = (value)=>{
    dispatch({ type: 'showDelete', payload: value });
  }
 
  return(
    <FeesTransactionContext.Provider value={{
      ...state, 
      dispatch, 
      getTransactions,
      setCurrentPage,
      filter,
      setSelectedIds,
      resetSelectedIds,
      getRecentTransactions,
      getBilledRatio,
      refresh,
      getBilledSum,
      getPaidSum,
      getDiscountedSum,
      getCollectionOverview,
      toggleDelete,
      getGrossBalanceRatio,
      getBillingSummary
      }}>
      {children}
    </FeesTransactionContext.Provider>
  )

}

export const useSchoolTransactions = ()=>useContext(FeesTransactionContext);

